import { Component, Prop, Event, EventEmitter, h } from '@stencil/core';
import { Element } from '@stencil/core';
import { isUndefined } from '../../utils/utils';

export interface SignetChipCloseEvent {
  image: string;
  label: string;
  closeable: boolean;
  id: string;
}

@Component({
  tag: 'signet-chip',
  styleUrl: 'signet-chip.scss',
})
export class SignetChip {
  @Element() el!: HTMLElement;
  /**
   * The unique identifier for the chip.
   */
  @Prop() chipId: string;
  /**
   * The style variant of the chip ('filled' or 'outlined').
   */
  @Prop() variant: 'filled' | 'outlined' = 'filled';
  /**
   * The URL of the image displayed in the chip.
   */
  @Prop() image: string;
  /**
   * The text label displayed in the chip.
   */
  @Prop() label: string;
  /**
   * Indicates whether the chip is closeable.
   */
  @Prop() closeable = false;

  @Event({ eventName: 'signet-chip-close' })
  close: EventEmitter;

  onClose() {
    const event: SignetChipCloseEvent = {
      image: this.image,
      label: this.label,
      closeable: this.closeable,
      id: this.chipId,
    };

    this.close.emit(event);
    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: event
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: event
    }));
  }

  render() {
    const hasImage = !!this.image && !isUndefined(this.image);

    const image = (() => {
      if (!hasImage) {
        return;
      }

      return (
        <div class="signet-chip-image">
          <img src={this.image} />
        </div>
      );
    })();

    const close = (() => {
      if (this.closeable) {
        return (
          <div class="signet-chip-close" onClick={this.onClose.bind(this)}>
            <signet-icon icon="add_circle_icon" class="close-icon" />
          </div>
        );
      }
    })();

    const chipClasses = {
      'signet-chip': true,
      [this.variant]: true,
      'signet-chip-extra-pd-lt': hasImage,
    };

    return (
      <div class={chipClasses}>
        {image}
        <span class="signet-chip-label">{this.label}</span>
        {close}
      </div>
    );
  }
}
